import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { addAlphaToHex } from '../../../models/utils/colorUtils';
import { LoadingLogo } from '../../../web/components/LoadingLogo';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { background, white } from '../../../web/styles/colors';
import { EventCard } from '../../components/EventCard';
import type { IEventLandingItem } from '../../queries/useEventsLanding';
import { getEventDateKey } from '../../utils/dateKey';

interface EventsCalendarListProps {
	events: IEventLandingItem[];
	month: Date;
	onMonthChange: (month: Date) => void;
	isLoading?: boolean;
}

interface EventGroup {
	date: string;
	dateLabel: string;
	events: IEventLandingItem[];
}

/**
 * Mobile calendar view component that displays events in a chronological list format.
 * Events are grouped by date with date headers.
 */
export const EventsCalendarList: React.FC<EventsCalendarListProps> = ({ events, month, onMonthChange, isLoading }) => {
	// Group events by date for the current month using timezone-aware date keys.
	// Uses the same getEventDateKey utility as EventsCalendar to ensure consistent
	// date grouping across mobile and desktop views.
	const eventGroups = React.useMemo(() => {
		const viewingYear = month.getFullYear();
		const viewingMonth = month.getMonth();
		const formatterCache = new Map<string, Intl.DateTimeFormat>();

		// Filter and group events that fall in the viewing month
		const grouped = new Map<string, IEventLandingItem[]>();
		events.forEach(event => {
			const dateKey = getEventDateKey(event, formatterCache);
			const [yearStr, monthStr] = dateKey.split('-');
			const eventYear = parseInt(yearStr, 10);
			const eventMonth = parseInt(monthStr, 10) - 1; // 0-indexed
			if (eventYear !== viewingYear || eventMonth !== viewingMonth) return;

			const existing = grouped.get(dateKey) || [];
			grouped.set(dateKey, [...existing, event]);
		});

		// Format date labels using noon to avoid any date-boundary issues
		const labelFormatter = new Intl.DateTimeFormat('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		});

		const groups: EventGroup[] = Array.from(grouped.entries())
			.map(([date, dateEvents]) => ({
				date,
				dateLabel: labelFormatter.format(new Date(date + 'T12:00:00')),
				events: dateEvents,
			}))
			.sort((a, b) => a.date.localeCompare(b.date));

		return groups;
	}, [events, month]);

	// Month navigation
	const handlePreviousMonth = () => {
		const prevMonth = moment(month).subtract(1, 'month').toDate();
		onMonthChange(prevMonth);
	};

	const handleNextMonth = () => {
		const nextMonth = moment(month).add(1, 'month').toDate();
		onMonthChange(nextMonth);
	};

	const monthLabel = moment(month).format('MMMM YYYY');
	const hasEvents = eventGroups.length > 0;

	return (
		<div className={css(styles.container)}>
			{isLoading && (
				<div className={css(styles.loadingOverlay)} role='status' aria-label='Loading events'>
					<LoadingLogo size={60} />
				</div>
			)}
			{/* Month Navigation */}
			<div className={css(styles.navigation)}>
				<Button onClick={handlePreviousMonth} variant='secondary' size='sm' aria-label='Previous month'>
					← Previous
				</Button>
				<h2 className={css(styles.monthHeading)}>{monthLabel}</h2>
				<Button onClick={handleNextMonth} variant='secondary' size='sm' aria-label='Next month'>
					Next →
				</Button>
			</div>

			{/* Event List */}
			{hasEvents ? (
				<div className={css(styles.eventsList)}>
					{eventGroups.map(group => (
						<div key={group.date} className={css(styles.dateGroup)}>
							<h3 className={css(styles.dateHeading)}>{group.dateLabel}</h3>
							<div className={css(styles.dateEvents)}>
								{group.events.map(event => (
									<EventCard key={event.id} event={event} />
								))}
							</div>
						</div>
					))}
				</div>
			) : (
				<div className={css(styles.noEvents)}>No events in {monthLabel}</div>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	container: {
		maxWidth: THEME.breakpoints.xl,
		boxSizing: 'border-box',
		margin: '0 auto',
		padding: `0 ${THEME.spacing.md}px`,
		position: 'relative',
		[THEME.queries.md]: {
			padding: '0 12px',
		},
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: addAlphaToHex(white, 0.8, background),
		zIndex: 10,
	},
	navigation: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: THEME.spacing.lg,
		gap: THEME.spacing.md,
		[THEME.queries.md]: {
			marginBottom: THEME.spacing.md,
		},
	},
	monthHeading: {
		fontSize: 20,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		textAlign: 'center',
		flex: 1,
		[THEME.queries.md]: {
			fontSize: THEME.typography.fontSizes.lg,
		},
	},
	eventsList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xl,
		[THEME.queries.md]: {
			gap: THEME.spacing.lg,
		},
	},
	dateGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
	dateHeading: {
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		paddingBottom: THEME.spacing.sm,
		borderBottom: `2px solid ${THEME.colors.border.main}`,
		[THEME.queries.md]: {
			fontSize: THEME.typography.fontSizes.md,
		},
	},
	dateEvents: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
	noEvents: {
		textAlign: 'center',
		fontSize: THEME.typography.fontSizes.md,
		color: THEME.colors.text.light,
		padding: `${THEME.spacing.xxl}px ${THEME.spacing.md}px`,
		[THEME.queries.md]: {
			fontSize: THEME.typography.fontSizes.sm,
			padding: `${THEME.spacing.xl}px 12px`,
		},
	},
});
